import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  BarChart3,
  Target,
  Loader2,
  AlertCircle,
  RefreshCw,
  Camera,
  Settings,
  Database,
  Wifi
} from 'lucide-react';
import { Scene3D } from './Scene3D';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { ShotComparison } from './ShotComparison';
import { useShotVisualizationStore } from '../stores/shotVisualizationStore';
import { ShotData } from '../types';
import { useSessionShots, useRecentShots } from '../hooks/useShots';
import { useLiveShots } from '@/hooks/useLiveShots';

interface ShotVisualizationSystemProps {
  className?: string;
  sessionId?: string; // Optional session ID to load specific session data
}

export const ShotVisualizationSystem: React.FC<ShotVisualizationSystemProps> = ({
  className = '',
  sessionId
}) => {
  const [selectedShotIndex, setSelectedShotIndex] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'3d' | 'analytics' | 'comparison'>('3d');
  const [dataSource, setDataSource] = useState<'session' | 'recent' | 'sample' | 'live'>('sample');
  const [localLoading, setLocalLoading] = useState(false);

  const {
    currentShot,
    shotComparison,
    loadShot,
    config,
    updateConfig
  } = useShotVisualizationStore();

  // Fetch data from database using React Query hooks
  const { 
    data: sessionShots, 
    isLoading: sessionLoading, 
    error: sessionError,
    refetch: refetchSession 
  } = useSessionShots(sessionId);
  
  const { 
    data: recentShots, 
    isLoading: recentLoading, 
    error: recentError,
    refetch: refetchRecent 
  } = useRecentShots(10, dataSource === 'recent' ? 5000 : undefined); // Auto-refresh every 5s
  
  // Live mode using WebSocket
  const {
    liveShots,
    lastShot: _lastShot,
    isLive,
    isConnected,
    simulateShot,
    clearShots,
    shotCount
  } = useLiveShots({
    sessionId,
    autoJoin: dataSource === 'live',
    onNewShot: (shot) => {
      // Auto-select the latest shot in live mode
      if (dataSource === 'live') {
        loadShot(shot);
      }
    }
  });

  // Sample data fallback with realistic cricket angles
  // Sweet spot is at Y = -0.32 (32cm below center)
  const calculateSweetSpotDistance = (x: number, y: number) => {
    const sweetSpotX = 0;
    const sweetSpotY = -0.32;
    return Math.sqrt(Math.pow(x - sweetSpotX, 2) + Math.pow(y - sweetSpotY, 2));
  };

  const sampleData: ShotData[] = [
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "straight-drive",
            quality: "good",
            ballSpeed: 35,
            batSpeed: 35,
            exitVelocity: 45,
            launchAngle: 8,  // Much flatter for a drive
            horizontalAngle: 0, // Straight towards bowler
            distance: 65,
            height: 4,  // Lower peak height
            spinRate: 2000,
            impactLocationX: 0.02,
            impactLocationY: -0.28, // Closer to sweet spot (4cm above it)
            sweetSpotDistance: calculateSweetSpotDistance(0.02, -0.28), // Will be ~0.045
            powerTransfer: 0.82,
            timing: "good",
            footwork: "good",
            balance: "good",
            followThrough: "complete"
          },
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "cover-drive",
            quality: "good",
            ballSpeed: 32,
            batSpeed: 35,
            exitVelocity: 48,
            launchAngle: 10,  // Flatter trajectory
            horizontalAngle: -35, // Off side
            distance: 65,
            height: 5,
            spinRate: 2000,
            impactLocationX: -0.02,
            impactLocationY: -0.30, // Near sweet spot (2cm above it)
            sweetSpotDistance: calculateSweetSpotDistance(-0.02, -0.30), // Will be ~0.028
            powerTransfer: 0.85,
            timing: "good",
            footwork: "good",
            balance: "good",
            followThrough: "partial"
          },
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "pull-shot",
            quality: "excellent",
            ballSpeed: 38,
            batSpeed: 40,
            exitVelocity: 55,
            launchAngle: 15,  // Pull shot slightly higher but not too much
            horizontalAngle: 75, // Leg side
            distance: 78,
            height: 8,
            spinRate: 2500,
            impactLocationX: 0.01,
            impactLocationY: -0.31, // Very close to sweet spot (1cm above it)
            sweetSpotDistance: calculateSweetSpotDistance(0.01, -0.31), // Will be ~0.014
            powerTransfer: 0.94,
            timing: "excellent",
            footwork: "excellent",
            balance: "good",
            followThrough: "complete"
          },
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "square-cut",
            quality: "good",
            ballSpeed: 36,
            batSpeed: 37,
            exitVelocity: 50,
            launchAngle: 5,  // Very flat for a cut shot
            horizontalAngle: -85, // Square on off side
            distance: 62,
            height: 2,
            spinRate: 1800,
            impactLocationX: -0.03,
            impactLocationY: -0.28, // 4cm above sweet spot
            sweetSpotDistance: calculateSweetSpotDistance(-0.03, -0.28), // Will be ~0.05
            powerTransfer: 0.82,
            timing: "good",
            footwork: "fair",
            balance: "good",
            followThrough: "complete"
          },
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "on-drive",
            quality: "good",
            ballSpeed: 33,
            batSpeed: 36,
            exitVelocity: 49,
            launchAngle: 10,  // Moderate height
            horizontalAngle: 30, // Mid-on direction
            distance: 68,
            height: 5,
            spinRate: 2100,
            impactLocationX: 0.02,
            impactLocationY: -0.30, // 2cm above sweet spot
            sweetSpotDistance: calculateSweetSpotDistance(0.02, -0.30), // Will be ~0.028
            powerTransfer: 0.88,
            timing: "good",
            footwork: "excellent",
            balance: "good",
            followThrough: "complete"
          },
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "sweep-shot",
            quality: "good",
            ballSpeed: 28,
            batSpeed: 32,
            exitVelocity: 44,
            launchAngle: 3,  // Very flat sweep
            horizontalAngle: 90, // Square leg
            distance: 55,
            height: 1,
            spinRate: 1600,
            impactLocationX: 0.03,
            impactLocationY: -0.37, // 5cm below sweet spot (lower on bat for sweep)
            sweetSpotDistance: calculateSweetSpotDistance(0.03, -0.37), // Will be ~0.058
            powerTransfer: 0.80,
            timing: "good",
            footwork: "good",
            balance: "fair",
            followThrough: "complete"
          },
          {
            sessionId: "session-001",
            tenantId: "sample-tenant",
            timestamp: new Date().toISOString(),
            playerId: "player-001",
            shotType: "lofted-drive",
            quality: "excellent",
            ballSpeed: 37,
            batSpeed: 42,
            exitVelocity: 56,
            launchAngle: 25,  // Lofted but not too high
            horizontalAngle: -15, // Slightly off side
            distance: 82,
            height: 15,
            spinRate: 2600,
            impactLocationX: 0,
            impactLocationY: -0.32, // Perfect sweet spot hit!
            sweetSpotDistance: calculateSweetSpotDistance(0, -0.32), // Will be 0
            powerTransfer: 0.96,
            timing: "excellent",
            footwork: "excellent",
            balance: "excellent",
            followThrough: "complete"
          }
  ];

  // Determine which data to use based on data source
  const shotData = 
    dataSource === 'session' && sessionShots ? sessionShots :
    dataSource === 'recent' && recentShots ? recentShots :
    dataSource === 'live' ? liveShots :
    sampleData;

  const loading = 
    dataSource === 'session' ? sessionLoading :
    dataSource === 'recent' ? recentLoading :
    dataSource === 'live' ? false :
    localLoading;

  const error = 
    dataSource === 'session' && sessionError ? sessionError.message :
    dataSource === 'recent' && recentError ? recentError.message :
    null;

  // Load the first shot when data changes
  useEffect(() => {
    if (shotData.length > 0 && !currentShot) {
      loadShot(shotData[0]);
    }
  }, [shotData, currentShot, loadShot]);

  const handleShotSelection = async (index: number) => {
    if (index >= 0 && index < shotData.length) {
      setSelectedShotIndex(index);
      setLocalLoading(true);
      try {
        await loadShot(shotData[index]);
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const handleRefreshData = () => {
    if (dataSource === 'session') {
      refetchSession();
    } else if (dataSource === 'recent') {
      refetchRecent();
    } else {
      // For sample data, just reload the first shot
      if (shotData.length > 0) {
        loadShot(shotData[0]);
      }
    }
  };

  const handleDataSourceChange = (source: 'session' | 'recent' | 'sample' | 'live') => {
    setDataSource(source);
    setSelectedShotIndex(0);
    
    // Clear live shots when switching away from live mode
    if (source !== 'live' && dataSource === 'live') {
      clearShots();
    }
  };

  if (loading && !shotData.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading shot data...</p>
        </div>
      </div>
    );
  }

  if (error && !shotData.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Data</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleRefreshData} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-6 w-6" />
              Cricket Shot Visualization & Optimization
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Data Source Selector */}
              <Select
                value={dataSource}
                onValueChange={(value) => handleDataSourceChange(value as 'session' | 'recent' | 'sample' | 'live')}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sample">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Sample Data
                    </div>
                  </SelectItem>
                  {sessionId && (
                    <SelectItem value="session">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        Session Data
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="recent">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Recent Shots
                    </div>
                  </SelectItem>
                  <SelectItem value="live">
                    <div className="flex items-center gap-2">
                      <Wifi className="h-4 w-4" />
                      Live Mode
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <Badge variant="outline">
                {shotData.length} shots
              </Badge>
              
              {dataSource === 'live' && (
                <>
                  <Badge variant="secondary" className={isConnected ? "animate-pulse bg-green-100" : "bg-red-100"}>
                    <Wifi className="h-3 w-3 mr-1" />
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </Badge>
                  {isLive && (
                    <Badge variant="outline">
                      {shotCount} shots
                    </Badge>
                  )}
                </>
              )}
              
              {dataSource === 'recent' && (
                <Badge variant="secondary">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Auto-refresh
                </Badge>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshData}
                className="flex items-center gap-2"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              {/* Test button for live mode (development only) */}
              {dataSource === 'live' && import.meta.env.DEV && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={simulateShot}
                  className="flex items-center gap-2"
                >
                  <Target className="h-4 w-4" />
                  Simulate Shot
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Shot Selection */}
            <div className="flex items-center gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Shot:</label>
                <Select
                  value={selectedShotIndex.toString()}
                  onValueChange={(value) => handleShotSelection(parseInt(value))}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Choose a shot to analyze" />
                  </SelectTrigger>
                  <SelectContent>
                    {shotData.map((shot, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {shot.shotType}
                          </Badge>
                          <span>{shot.distance.toFixed(1)}m</span>
                          <Badge 
                            variant="secondary" 
                            className={
                              shot.quality === 'excellent' ? 'bg-green-100 text-green-800' :
                              shot.quality === 'good' ? 'bg-yellow-100 text-yellow-800' :
                              shot.quality === 'fair' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }
                          >
                            {shot.quality}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {loading && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </div>

            {/* View Mode Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">View Mode:</label>
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                <Button
                  variant={viewMode === '3d' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('3d')}
                  className="flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" />
                  3D View
                </Button>
                <Button
                  variant={viewMode === 'analytics' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('analytics')}
                  className="flex items-center gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </Button>
                <Button
                  variant={viewMode === 'comparison' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('comparison')}
                  className="flex items-center gap-2"
                >
                  <Target className="h-4 w-4" />
                  Comparison
                </Button>
              </div>
            </div>
          </div>

          {/* Current Shot Info */}
          {currentShot && (
            <div className="mt-4 pt-4 border-t">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Shot Type</p>
                  <p className="font-medium capitalize">{currentShot.shotType}</p>
                </div>
                <div>
                  <p className="text-gray-600">Quality</p>
                  <Badge 
                    variant="secondary" 
                    className={
                      currentShot.quality === 'excellent' ? 'bg-green-100 text-green-800' :
                      currentShot.quality === 'good' ? 'bg-yellow-100 text-yellow-800' :
                      currentShot.quality === 'fair' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }
                  >
                    {currentShot.quality.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-gray-600">Distance</p>
                  <p className="font-medium">{currentShot.distance.toFixed(1)} m</p>
                </div>
                <div>
                  <p className="text-gray-600">Exit Velocity</p>
                  <p className="font-medium">{currentShot.exitVelocity.toFixed(1)} m/s</p>
                </div>
                <div>
                  <p className="text-gray-600">Launch Angle</p>
                  <p className="font-medium">{currentShot.launchAngle.toFixed(1)}°</p>
                </div>
                <div>
                  <p className="text-gray-600">Power Transfer</p>
                  <p className="font-medium">{(currentShot.powerTransfer * 100).toFixed(1)}%</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content Area */}
      {viewMode === '3d' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* 3D Visualization */}
          <div className="xl:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  3D Shot Visualization
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[600px] rounded-lg overflow-hidden">
                  <Scene3D />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side Panel with Quick Analytics */}
          <div className="space-y-6">
            {shotComparison && (
              <>
                {/* Quick Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Potential Gain</span>
                      <span className="font-bold text-green-600">
                        +{shotComparison.optimization.potentialDistanceGain.toFixed(1)}m
                      </span>
                    </div>
                    <Separator />
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Launch Angle</span>
                        <span className={
                          Math.abs(shotComparison.optimization.improvements.launchAngleDelta) < 2
                            ? 'text-green-600' : 'text-amber-600'
                        }>
                          {shotComparison.optimization.improvements.launchAngleDelta > 0 ? '+' : ''}
                          {shotComparison.optimization.improvements.launchAngleDelta.toFixed(1)}°
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Exit Velocity</span>
                        <span className={
                          shotComparison.optimization.improvements.velocityDelta < 2
                            ? 'text-green-600' : 'text-amber-600'
                        }>
                          {shotComparison.optimization.improvements.velocityDelta > 0 ? '+' : ''}
                          {shotComparison.optimization.improvements.velocityDelta.toFixed(1)} m/s
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sweet Spot</span>
                        <span className={
                          currentShot?.sweetSpotDistance && currentShot.sweetSpotDistance < 0.02
                            ? 'text-green-600' : 'text-red-600'
                        }>
                          {currentShot?.sweetSpotDistance && currentShot.sweetSpotDistance < 0.02 ? 'Hit' : 'Missed'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Recommendations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Key Improvements</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {shotComparison.optimization.recommendations.slice(0, 3).map((rec, index) => (
                        <div key={index} className="text-sm text-gray-700 bg-blue-50 p-2 rounded">
                          {rec}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      )}

      {viewMode === 'analytics' && shotComparison && (
        <AnalyticsDashboard shotComparison={shotComparison} />
      )}

      {viewMode === 'comparison' && shotComparison && (
        <ShotComparison shotComparison={shotComparison} />
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Error: {error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Visualization Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showActual"
                checked={config.showActualTrajectory}
                onChange={(e) => updateConfig({ showActualTrajectory: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="showActual" className="text-sm">Actual Trajectory</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showOptimal"
                checked={config.showOptimalTrajectory}
                onChange={(e) => updateConfig({ showOptimalTrajectory: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="showOptimal" className="text-sm">Optimal Trajectory</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showMetrics"
                checked={config.showMetrics}
                onChange={(e) => updateConfig({ showMetrics: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="showMetrics" className="text-sm">Annotations</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showField"
                checked={config.showField}
                onChange={(e) => updateConfig({ showField: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="showField" className="text-sm">Cricket Field</label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};